import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PhoneInput, isValidPhoneNumber } from '@/components/ui/phone-input';
import { useCriarCliente } from '@/hooks/useClientes';
import { useServicosAtivos } from '@/hooks/useServicos';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2, MapPin, User } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

interface ClienteFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface FormData {
  nome: string;
  telefone: string;
  email: string;
  servicoInteresse: string;
  endereco_rua: string;
  endereco_numero: string;
  endereco_complemento: string;
  endereco_codigo_postal: string;
  endereco_cidade: string;
  endereco_regiao: string;
  endereco_pais: string;
}

const initialFormData: FormData = {
  nome: '',
  telefone: '',
  email: '',
  servicoInteresse: '',
  endereco_rua: '',
  endereco_numero: '',
  endereco_complemento: '',
  endereco_codigo_postal: '',
  endereco_cidade: '',
  endereco_regiao: '',
  endereco_pais: '',
};


export function ClienteFormDialog({ open, onOpenChange }: ClienteFormDialogProps) {
  const { t } = useTranslation();
  const { organization } = useAuthContext();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [erros, setErros] = useState<{ telefone?: string; email?: string }>({});

  const criarCliente = useCriarCliente();
  const { data: servicosAtivos } = useServicosAtivos();

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (erros[field as keyof typeof erros]) {
      setErros(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validar = () => {
    const novosErros: { telefone?: string; email?: string } = {};
    
    if (!formData.telefone) {
      novosErros.telefone = t('common.validation.phoneRequired');
    } else if (!isValidPhoneNumber(formData.telefone)) {
      novosErros.telefone = t('common.validation.invalidPhone');
    }

    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      novosErros.email = t('common.validation.invalidEmail');
    }

    setErros(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validar()) return;

    criarCliente.mutate({
      nome: formData.nome.trim() || null,
      telefone: formData.telefone, // Já vem formatado em E.164
      email: formData.email.trim() || null,
      servico_interesse: formData.servicoInteresse || null,
      origem: 'Manual',
      status: 'novo',
      endereco_rua: formData.endereco_rua.trim() || null,
      endereco_numero: formData.endereco_numero.trim() || null,
      endereco_complemento: formData.endereco_complemento.trim() || null,
      endereco_codigo_postal: formData.endereco_codigo_postal.trim() || null,
      endereco_cidade: formData.endereco_cidade.trim() || null,
      endereco_pais: formData.endereco_pais.trim() || null,
    }, {
      onSuccess: () => {
        onOpenChange(false);
        resetForm();
      },
    });
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setErros({});
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) resetForm();
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] !overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('clients.addClient')}</DialogTitle>
          <DialogDescription>
            {t('clients.createClientDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Seção: Dados Pessoais */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <User className="h-4 w-4" />
              <span>{t('clients.form.personalData')}</span>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="nome">{t('clients.form.name')}</Label>
              <Input
                id="nome"
                placeholder={t('clients.form.namePlaceholder')}
                value={formData.nome}
                onChange={(e) => updateField('nome', e.target.value)}
                maxLength={100}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="telefone">{t('clients.form.phone')} *</Label>
                <PhoneInput
                  value={formData.telefone}
                  onChange={(value) => updateField('telefone', value)}
                  defaultCountry="PT"
                  placeholder="912 345 678"
                  error={!!erros.telefone}
                />
                {erros.telefone && (
                  <p className="text-sm text-destructive">{erros.telefone}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('clients.form.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => updateField('email', e.target.value)}
                  maxLength={255}
                  className={erros.email ? 'border-destructive' : ''}
                />
                {erros.email && (
                  <p className="text-sm text-destructive">{erros.email}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="servico">{t('clients.form.serviceOfInterest')}</Label>
              <Select
                value={formData.servicoInteresse}
                onValueChange={(v) => updateField('servicoInteresse', v)}
              >
                <SelectTrigger id="servico">
                  <SelectValue placeholder={t('clients.form.selectService')} />
                </SelectTrigger>
                <SelectContent>
                  {(servicosAtivos ?? []).map((s) => (
                    <SelectItem key={s.id} value={s.nome}>
                      {s.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Seção: Endereço */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <MapPin className="h-4 w-4" />
              <span>{t('clients.form.address')}</span>
            </div>

            {/* Linha 1: Rua + Número */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="endereco_rua">{t('clients.form.street')}</Label>
                <Input
                  id="endereco_rua"
                  placeholder={t('clients.form.streetPlaceholder')}
                  value={formData.endereco_rua}
                  onChange={(e) => updateField('endereco_rua', e.target.value)}
                  maxLength={200}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco_numero">{t('clients.form.number')}</Label>
                <Input
                  id="endereco_numero"
                  placeholder={t('clients.form.numberPlaceholder')}
                  value={formData.endereco_numero}
                  onChange={(e) => updateField('endereco_numero', e.target.value)}
                  maxLength={20}
                />
              </div>
            </div>

            {/* Linha 2: Complemento */}
            <div className="space-y-2">
              <Label htmlFor="endereco_complemento">{t('clients.form.complement')}</Label>
              <Input
                id="endereco_complemento"
                placeholder={t('clients.form.complementPlaceholder')}
                value={formData.endereco_complemento}
                onChange={(e) => updateField('endereco_complemento', e.target.value)}
                maxLength={100}
              />
            </div>

            {/* Linha 3: Código Postal + Cidade */}
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="endereco_codigo_postal">{t('clients.form.postalCode')}</Label>
                <Input
                  id="endereco_codigo_postal"
                  placeholder="0000-000"
                  value={formData.endereco_codigo_postal}
                  onChange={(e) => updateField('endereco_codigo_postal', e.target.value)}
                  maxLength={20}
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="endereco_cidade">{t('clients.form.city')}</Label>
                <Input
                  id="endereco_cidade"
                  placeholder="Lisboa"
                  value={formData.endereco_cidade}
                  onChange={(e) => updateField('endereco_cidade', e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>

            {/* Linha 4: Região + País */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="endereco_regiao">{t('clients.form.region')}</Label>
                <Input
                  id="endereco_regiao"
                  placeholder="Distrito de Lisboa"
                  value={formData.endereco_regiao}
                  onChange={(e) => updateField('endereco_regiao', e.target.value)}
                  maxLength={100}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="endereco_pais">{t('clients.form.country')}</Label>
                <Input
                  id="endereco_pais"
                  placeholder={organization?.pais ?? ''}
                  value={formData.endereco_pais}
                  onChange={(e) => updateField('endereco_pais', e.target.value)}
                  maxLength={100}
                />
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={criarCliente.isPending}>
              {criarCliente.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t('clients.createClient')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
